import type { UserStatus } from "@/lib/api-types";
import { cx } from "@/lib/cx";
import { badgeFor, type BadgeFill } from "@/lib/status-badge";

/** DESIGN.md § Components `badge-status`: 24px, `rounded.sm`, `typography.meta`. */
const FILL_CLASSES: Record<BadgeFill, string> = {
  neutral: "bg-neutral-quiet text-ink-secondary",
  attention: "bg-warning-quiet text-warning",
  overdue: "bg-danger-quiet text-danger",
  settled: "bg-success-quiet text-success",
};

/**
 * The one badge primitive every status badge actually renders through — a
 * plain-language `label` plus a `fill` (never colour alone, EXPERIENCE.md
 * § Accessibility Floor). `StatusBadge` below is this exact markup,
 * specialized for the one caller with a `UserStatus` to map through
 * `badgeFor`; Story 4.5's Backup screen is the first caller with no
 * `UserStatus` at all (a boolean `is_overdue`, computed server-side) —
 * reusing this primitive directly, through its own `fill: "overdue"`
 * (already defined, never yet exercised by a real screen), keeps that
 * screen to "no new visual primitive" rather than a second, duplicated
 * badge implementation.
 */
export function Badge({ label, fill }: { label: string; fill: BadgeFill }) {
  return (
    <span
      className={cx(
        "inline-flex h-badge items-center rounded-sm px-2 text-meta font-normal",
        FILL_CLASSES[fill],
      )}
    >
      {label}
    </span>
  );
}

/**
 * Always carries its status word (never colour alone, EXPERIENCE.md
 * § Accessibility Floor). The fill/label mapping is `lib/status-badge.ts`'s
 * job, the one place a `UserStatus` literal is named — this component takes
 * whatever `status` a row actually carries and never enumerates the
 * possibilities itself.
 */
export function StatusBadge({ status }: { status: UserStatus }) {
  const { label, fill } = badgeFor(status);
  return <Badge label={label} fill={fill} />;
}
